import React from 'react';
import { useLanguage } from '@contexts/LanguageContext';
import { formatDate, getDurationText } from '@utils/dateUtils';
import Icon from '@components/Icon/Icon';
import { faUser, faCalendar, faClipboard, faArrowUp, faClock, faArrowDown, faStopwatch, faClockRotateLeft, faPencil, faListCheck } from '@fortawesome/free-solid-svg-icons';

export const BorrowingProperties = ({ borrowing, currentLanguage }) => {
    const { t } = useLanguage();

    const formatDateWithLocale = (dateString, includeTime = true, includeDayName = false) => {
        return formatDate(dateString, includeTime, currentLanguage, t, true, includeDayName);
    };

    const getDurationTextWithLocale = (startDate, endDate) => {
        return getDurationText(startDate, endDate, t, currentLanguage);
    };

    // Helper function to format dynamic field values based on type
    const formatDynamicFieldValue = (value, type) => {
        if (value === null || value === undefined) return '-';

        switch (type) {
            case 'boolean':
                return value ? t('borrowingFields.yes') : t('borrowingFields.no');
            case 'integer':
            case 'float':
                return String(value);
            case 'string':
            case 'select':
            default:
                return value;
        }
    };

    // Helper function to get the field definition from borrowing's resource category
    const getFieldDefinition = (fieldKey) => {
        if (!borrowing.resource?.category?.borrowing_field_definitions) return null;
        // borrowing_field_definitions is a dictionary/object, not an array
        return borrowing.resource.category.borrowing_field_definitions[fieldKey];
    };

    return (
        <div className="properties-section">
            <h3>{t('borrowing.details')}</h3>
            <div className="properties-grid">
                {borrowing.user && (
                    <div className="property-card">
                        <span className="property-icon">
                            <Icon icon={faUser} color="#4A90E2" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.borrower')}</label>
                            <span>{borrowing.user.name || borrowing.user.email}</span>
                        </div>
                    </div>
                )}

                {borrowing.reserved_from && borrowing.reserved_until && (
                    <div className="property-card">
                        <span className="property-icon">
                            <Icon icon={faCalendar} color="#7B68EE" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.reservedFromTo')}</label>
                            <div className="reservation-period">
                                <div>{formatDateWithLocale(borrowing.reserved_from, true, true)}</div>
                                <div className="arrow">→</div>
                                <div>{formatDateWithLocale(borrowing.reserved_until, true, true)}</div>
                            </div>
                        </div>
                    </div>
                )}

                {borrowing.reservation_date && (
                    <div className="property-card">
                        <span className="property-icon">
                            <Icon icon={faClipboard} color="#50C878" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.reservationDate')}</label>
                            <span>{formatDateWithLocale(borrowing.reservation_date)}</span>
                        </div>
                    </div>
                )}

                {borrowing.borrowed_at && (
                    <div className="property-card">
                        <span className="property-icon">
                            <Icon icon={faArrowUp} color="#FF6B35" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.borrowedAt')}</label>
                            <span>{formatDateWithLocale(borrowing.borrowed_at)}</span>
                        </div>
                    </div>
                )}

                {borrowing.expected_return_at && (
                    <div className="property-card">
                        <span className="property-icon">
                            <Icon icon={faClock} color="#dfbf0bff" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.expectedReturnAt')}</label>
                            <span>{formatDateWithLocale(borrowing.expected_return_at)}</span>
                        </div>
                    </div>
                )}

                {borrowing.returned_at && (
                    <div className="property-card">
                        <span className="property-icon">
                            <Icon icon={faArrowDown} color="#32CD32" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.returnedAt')}</label>
                            <span>{formatDateWithLocale(borrowing.returned_at)}</span>
                        </div>
                    </div>
                )}

                {borrowing.borrowed_at && (
                    <div className="property-card">
                        <span className="property-icon">
                            <Icon icon={faStopwatch} color="#9370DB" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.duration')}</label>
                            <span>{getDurationTextWithLocale(borrowing.borrowed_at, borrowing.returned_at)}</span>
                        </div>
                    </div>
                )}

                {borrowing.created_at && (
                    <div className="property-card property-card--secondary">
                        <span className="property-icon">
                            <Icon icon={faClockRotateLeft} color="#708090" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.createdAt')}</label>
                            <span>{formatDateWithLocale(borrowing.created_at)}</span>
                        </div>
                    </div>
                )}

                {borrowing.updated_at && (
                    <div className="property-card property-card--secondary">
                        <span className="property-icon">
                            <Icon icon={faPencil} color="#708090" />
                        </span>
                        <div className="property-content">
                            <label>{t('borrowingView.updatedAt')}</label>
                            <span>{formatDateWithLocale(borrowing.updated_at)}</span>
                        </div>
                    </div>
                )}
            </div>

            {/* Dynamic Fields Section */}
            {borrowing.dynamic_fields && Object.keys(borrowing.dynamic_fields).length > 0 && (
                <div className="dynamic-fields-section">
                    <h4 className="dynamic-fields-title">
                        <Icon icon={faListCheck} />
                        <span>{t('borrowingFields.additionalInformation')}</span>
                    </h4>
                    <div className="properties-grid">
                        {Object.entries(borrowing.dynamic_fields).map(([fieldKey, fieldData]) => {
                            // Extract value and definition from the new structure
                            const fieldValue = fieldData?.value !== undefined ? fieldData.value : fieldData;
                            const fieldDef = fieldData?.definition || getFieldDefinition(fieldKey);

                            // Fallback: display even if definition is not found
                            const label = fieldDef?.label || fieldKey;
                            const type = fieldDef?.type || 'string';

                            return (
                                <div key={fieldKey} className="property-card property-card--dynamic">
                                    <div className="property-content">
                                        <label>{label}</label>
                                        <span>{formatDynamicFieldValue(fieldValue, type)}</span>
                                        {fieldDef?.help_text && (
                                            <small className="field-help">{fieldDef.help_text}</small>
                                        )}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}
        </div>
    );
};
